/**
 * FAQ live filtering
 * ---------------------------------------------------------------------------
 * Filters questions as you type, highlights the matched term, and hides group
 * headings that have no visible questions left under them.
 *
 * Everything is already in the DOM, so this is pure show/hide — no requests,
 * and the full FAQ stays crawlable and readable with JS disabled.
 */

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Wraps matches in <mark> without ever touching innerHTML on untrusted input:
 * walks text nodes only, so existing markup and links stay intact.
 */
function highlightIn(element, term) {
  clearHighlight(element);
  if (!term) return;

  const pattern = new RegExp(escapeRegex(term), 'ig');
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const targets = [];

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.nodeValue && pattern.test(node.nodeValue)) targets.push(node);
    pattern.lastIndex = 0;
  }

  targets.forEach((node) => {
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    pattern.lastIndex = 0;
    while ((match = pattern.exec(node.nodeValue)) !== null) {
      fragment.appendChild(document.createTextNode(node.nodeValue.slice(lastIndex, match.index)));
      const mark = document.createElement('mark');
      mark.textContent = match[0];
      fragment.appendChild(mark);
      lastIndex = match.index + match[0].length;
      if (match.index === pattern.lastIndex) pattern.lastIndex += 1; // guard zero-length
    }

    fragment.appendChild(document.createTextNode(node.nodeValue.slice(lastIndex)));
    node.parentNode.replaceChild(fragment, node);
  });
}

function clearHighlight(element) {
  element.querySelectorAll('mark').forEach((mark) => {
    mark.replaceWith(document.createTextNode(mark.textContent));
  });
  element.normalize();
}

function setupFaq(section) {
  const input = section.querySelector('[data-faq-search]');
  const list = section.querySelector('[data-faq-list]');
  const emptyMessage = section.querySelector('[data-faq-empty]');
  if (!input || !list) return;

  const items = Array.from(list.querySelectorAll('[data-faq-item]'));
  const groups = Array.from(list.querySelectorAll('[data-faq-group]'));

  const filter = () => {
    const term = input.value.trim().toLowerCase();
    let visible = 0;

    items.forEach((item) => {
      const question = item.querySelector('[data-faq-question]');
      const answer = item.querySelector('[data-faq-answer]');
      const haystack = `${question?.textContent || ''} ${answer?.textContent || ''}`.toLowerCase();
      const match = !term || haystack.includes(term);

      item.hidden = !match;
      if (match) visible += 1;

      // Open matches so the shopper sees the answer without another click.
      if (term && match) item.open = true;
      else if (!term) item.open = false;

      if (question) highlightIn(question, term && match ? input.value.trim() : '');
    });

    // Hide a group heading once everything beneath it is filtered out.
    groups.forEach((group) => {
      let node = group.nextElementSibling;
      let anyVisible = false;
      while (node && !node.hasAttribute('data-faq-group')) {
        if (node.hasAttribute('data-faq-item') && !node.hidden) {
          anyVisible = true;
          break;
        }
        node = node.nextElementSibling;
      }
      group.hidden = !anyVisible;
    });

    if (emptyMessage) emptyMessage.hidden = visible !== 0;
  };

  let timer;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(filter, 120);
  });

  // Escape clears the field, matching every other search box on the site.
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    input.value = '';
    filter();
  });
}

function init() {
  document.querySelectorAll('.wb-faq').forEach(setupFaq);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
